import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { Flower2, ArrowLeft, Calendar, Share2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { API } from '@/App';

function ArticlePage() {
  const navigate = useNavigate();
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchArticle();
  }, [slug]);

  useEffect(() => {
    // Update page title and meta for SEO
    if (article) {
      document.title = `${article.title} | Radiant 40`;
      
      // Update meta description
      let metaDesc = document.querySelector('meta[name="description"]');
      if (metaDesc) {
        metaDesc.setAttribute('content', article.content.substring(0, 160));
      }
      
      // Update OG tags
      let ogTitle = document.querySelector('meta[property="og:title"]');
      if (ogTitle) ogTitle.setAttribute('content', article.title);
      
      let ogDesc = document.querySelector('meta[property="og:description"]');
      if (ogDesc) ogDesc.setAttribute('content', article.content.substring(0, 160));
      
      let ogImage = document.querySelector('meta[property="og:image"]');
      if (ogImage && article.featured_image) {
        ogImage.setAttribute('content', article.featured_image);
      }
    }
    
    return () => {
      document.title = 'Radiant 40 - 12-Week Wellness Journey';
    };
  }, [article]);

  const fetchArticle = async () => {
    try {
      const response = await axios.get(`${API}/articles/${slug}`);
      setArticle(response.data);
    } catch (error) {
      toast.error('Article not found');
      navigate('/articles');
    }
    setLoading(false);
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const shareArticle = () => {
    // Use the share URL that has proper OG tags for social media
    // This URL redirects users to the actual article page
    const baseUrl = window.location.origin;
    const shareUrl = `${baseUrl}/api/share/${slug}`;
    
    if (navigator.share) {
      navigator.share({
        title: article.title,
        text: article.content?.substring(0, 100) + '...',
        url: shareUrl
      });
    } else {
      navigator.clipboard.writeText(shareUrl);
      toast.success('Article link copied! Ready to share on social media.');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary-50 to-secondary-50">
        <div className="animate-pulse text-primary">Loading article...</div>
      </div>
    );
  }

  if (!article) return null;

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#FFF0F5' }}>
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md shadow-sm sticky top-0 z-50">
        <div className="container mx-auto px-6 py-4 flex items-center justify-between">
          <button 
            onClick={() => navigate('/')}
            className="flex items-center gap-2 hover:opacity-80 transition-colors"
          >
            <Flower2 className="w-8 h-8 text-primary" />
            <span className="text-xl font-serif font-bold" style={{ color: '#1e3a5f' }}>Radiant 40</span>
          </button>
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/articles')}
              style={{ color: '#1e3a5f' }}
              className="hover:text-primary transition-colors"
            >
              All Articles
            </button>
            <Button
              onClick={() => navigate('/auth')}
              variant="outline"
              className="border-2 border-primary text-primary hover:bg-primary hover:text-white rounded-full"
            >
              Sign In
            </Button>
          </div>
        </div>
      </header>

      {/* Article Content */}
      <main className="container mx-auto px-6 py-12">
        <article className="max-w-3xl mx-auto">
          <button
            onClick={() => navigate('/articles')}
            className="flex items-center gap-2 hover:text-primary mb-8 transition-colors"
            style={{ color: '#1e3a5f' }}
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Articles
          </button>

          {article.featured_image && (
            <img
              src={article.featured_image}
              alt={article.title}
              className="w-full h-64 md:h-96 object-cover rounded-2xl mb-8"
            />
          )}

          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-2" style={{ color: '#1e3a5f', opacity: 0.7 }}>
              <Calendar className="w-4 h-4" />
              {formatDate(article.created_at)}
            </div>
            <button
              onClick={shareArticle}
              className="flex items-center gap-2 hover:text-primary transition-colors"
              style={{ color: '#1e3a5f', opacity: 0.7 }}
            >
              <Share2 className="w-4 h-4" />
              Share
            </button>
          </div>

          <h1 className="text-3xl md:text-5xl font-serif font-bold mb-8" style={{ color: '#1e3a5f' }}>
            {article.title}
          </h1>

          <div 
            className="prose prose-lg max-w-none"
            style={{ color: '#1e3a5f' }}
            dangerouslySetInnerHTML={{ __html: article.content }}
          />

          {/* CTA */}
          <div className="mt-12 p-8 bg-gradient-to-r from-primary/10 to-accent/10 rounded-2xl text-center">
            <h3 className="text-2xl font-serif font-bold mb-4" style={{ color: '#1e3a5f' }}>
              Ready to Start Your Wellness Journey?
            </h3>
            <p className="mb-6" style={{ color: '#1e3a5f', opacity: 0.8 }}>
              Join Radiant 40 and transform your mind, body, and spirit in just 12 weeks.
            </p>
            <Button
              onClick={() => navigate('/register-free')}
              className="bg-primary hover:bg-primary/90 text-white px-8 py-3 rounded-full"
            >
              Get Started Free
            </Button>
          </div>
        </article>
      </main>

      {/* Footer */}
      <footer className="py-8 mt-12" style={{ backgroundColor: 'rgba(255,255,255,0.5)' }}>
        <div className="container mx-auto px-6 text-center" style={{ color: '#1e3a5f', opacity: 0.6 }}>
          <p>© 2026 Sundog Rosie. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}

export default ArticlePage;
